import React from 'react';
import type { LeadActivityDeleteScope } from './LeadActivityDeleteDialog';
import type { Lead } from '../../../../types';
import { AddressAutocompleteInput } from '../../../../components/AddressAutocompleteInput';
import {
  ActivityDayCalendar,
  type ActivityCalendarEntry,
} from '../../../../components/activities/activityDayCalendar';
import { type SchedulingStatus } from '../../../../components/plantafel/plantafelTypes';
import { FALLBACK_ACTIVITY_TYPES } from '../../../../utils/activityIcons';
import {
  localWallTimeToIso,
  formatWallClockYmd,
  isoToLocalDateAndTime,
  addCalendarDaysYmd,
} from '../../../../utils/activityDateTime';
import { useChecklistsForSelect } from '../../../../hooks/useChecklistsForSelect';
import { buildChecklistSelectOptions } from '../../../../utils/checklistSelectOptions';
import { getDefaultChecklistIdForActivityType } from '../../../../utils/activityTypeDefaultChecklist';
import { parseEmployeesListResponse } from '../../../../utils/parseEmployeesApi';
import { filterActivitiesForEinsatzteam } from './leadActivityHelpers';
import type { LeadActivity } from '../../../../pages/LeadDetailPage';
import type { MentionMember } from '../../../../utils/notesMentions';
import {
  defaultRecurrenceForm,
  recurrenceFormFromStored,
} from '../../../../utils/activityRecurrence';
import { recurrencePayloadFromForm } from '../../../../components/activities/ActivityRecurrenceFields';
import { useAssigneeConflictConfirm } from '../../../../components/activities/useAssigneeConflictConfirm';
import {
  availabilityBadge,
  unavailableAssignees,
  useAssigneeAvailability,
} from '../../../../components/activities/useAssigneeAvailability';
import { APP_INPUT_CLASS } from '../../../../ui/controlStyles';
import { useCapabilities } from '../../../../hooks/useCapabilities';

export type InstanceMemberOption = MentionMember;

/** Aktivitäten eines Leads: Liste, CRUD, Formularstatus, Typen/Checklisten/Zuweisungen für das Formular. */
export function useLeadActivities(
  lead: Lead | null,
  opts: { timeZone: string; timeZoneReady: boolean; leadAssigneeUserIds: string[] },
) {
  const { timeZone, timeZoneReady, leadAssigneeUserIds } = opts;
  const activityFormSeededTzRef = React.useRef(timeZone);

  const [activities, setActivities] = React.useState<LeadActivity[]>([]);
  const [instanceMembers, setInstanceMembers] = React.useState<InstanceMemberOption[]>([]);
  const [employeesForAssign, setEmployeesForAssign] = React.useState<
    { id: string; first_name: string; last_name: string }[]
  >([]);
  const [activityMenuOpen, setActivityMenuOpen] = React.useState<string | null>(null);
  // Einsatzteam aus dem geteilten Mitarbeiter-Picker (Mitarbeiter, Fuhrpark oder Projekte bearbeiten).
  const { can, loaded: capabilitiesLoaded } = useCapabilities();
  const canPickEmployees = can('web.hr.view') || can('web.fuhrpark.view') || can('web.projekte.edit');

  React.useEffect(() => {
    fetch('/api/me/members', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { members: [] }))
      .then((data) => {
        const list = Array.isArray(data?.members) ? data.members : [];
        setInstanceMembers(
          list
            .map(
              (m: {
                user_id?: string;
                email?: string;
                display_name?: string | null;
                username?: string | null;
              }) => ({
                user_id: String(m.user_id ?? ''),
                email: String(m.email ?? ''),
                display_name: m.display_name ?? null,
                username: m.username ?? null,
              }),
            )
            .filter((m: InstanceMemberOption) => m.user_id.length > 0),
        );
      })
      .catch(() => setInstanceMembers([]));
  }, []);

  React.useEffect(() => {
    if (!capabilitiesLoaded) return;
    if (!canPickEmployees) {
      setEmployeesForAssign([]);
      return;
    }
    fetch('/api/employees/picker', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setEmployeesForAssign(parseEmployeesListResponse(data)))
      .catch(() => setEmployeesForAssign([]));
  }, [capabilitiesLoaded, canPickEmployees]);

  const activityUserSelectOptions = React.useMemo(
    () =>
      instanceMembers.map((m) => ({
        id: m.user_id,
        display_name: m.display_name,
        email: m.email,
      })),
    [instanceMembers],
  );

  const [editingActivity, setEditingActivity] = React.useState<LeadActivity | null>(null);
  const [activitySaveError, setActivitySaveError] = React.useState<string | null>(null);

  const activityEmployeeBaseOptions = React.useMemo(() => {
    const byId = new Map(
      employeesForAssign.map((e) => [
        e.id,
        {
          id: e.id,
          name: `${e.first_name} ${e.last_name}`.trim() || 'Mitarbeiter',
        },
      ]),
    );
    // Keep historically assigned (possibly soft-deleted) employees visible while editing.
    for (const e of editingActivity?.assignee_employees ?? []) {
      if (byId.has(e.id)) continue;
      const name = `${e.first_name} ${e.last_name}`.trim();
      byId.set(e.id, {
        id: e.id,
        name: name ? `${name} (entfernt)` : 'Mitarbeiter (entfernt)',
      });
    }
    return [...byId.values()];
  }, [employeesForAssign, editingActivity]);

  const [scheduledActivities, setScheduledActivities] = React.useState<ActivityCalendarEntry[]>([]);
  React.useEffect(() => {
    fetch('/api/activities/scheduled', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setScheduledActivities(Array.isArray(data) ? data : []))
      .catch(() => setScheduledActivities([]));
  }, []);

  const [activityTypesFromApi, setActivityTypesFromApi] = React.useState<
    { id: string; label: string; icon: string; sort_order?: number; default_checklist_id?: string | null }[]
  >([]);
  const [activityTypesLoaded, setActivityTypesLoaded] = React.useState(false);

  React.useEffect(() => {
    fetch('/api/settings/activity-types')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        setActivityTypesFromApi(Array.isArray(data) ? data : []);
        setActivityTypesLoaded(true);
      })
      .catch(() => setActivityTypesLoaded(true));
  }, []);

  const activityTypesList =
    activityTypesLoaded && activityTypesFromApi.length > 0
      ? activityTypesFromApi
      : FALLBACK_ACTIVITY_TYPES.map((t) => ({
          id: t.id,
          label: t.label,
          icon: t.icon,
          sort_order: 0,
          default_checklist_id: null as string | null,
        }));

  const checklistsForSelect = useChecklistsForSelect();

  const [activityForm, setActivityForm] = React.useState(() => {
    const todayKey = formatWallClockYmd(new Date(), timeZone);
    const initialType = activityTypesList[0]?.id ?? 'call';
    return {
      title: '',
      activity_type: initialType,
      checklist_id: getDefaultChecklistIdForActivityType(activityTypesList, initialType) as string | null,
      dueDate: todayKey,
      dueTimeStart: '09:00',
      dueTimeEnd: '10:00',
      endDate: todayKey,
      scheduling_status: 'geplant' as SchedulingStatus,
      notes: '',
      site_address: (lead?.baustelle?.address ?? lead?.site_address ?? '').trim(),
      assignee_user_ids: [] as string[],
      assignee_employee_ids: [] as string[],
      recurrence: defaultRecurrenceForm(todayKey),
    };
  });

  // Re-seed date defaults once company TZ is known (or changes), if user has not edited them and is not editing an activity.
  React.useEffect(() => {
    if (!timeZoneReady) return;
    if (activityFormSeededTzRef.current === timeZone) return;
    if (editingActivity) {
      activityFormSeededTzRef.current = timeZone;
      return;
    }
    setActivityForm((f) => {
      const oldToday = formatWallClockYmd(new Date(), activityFormSeededTzRef.current);
      const untouched =
        f.dueDate === oldToday &&
        f.endDate === oldToday &&
        f.dueTimeStart === '09:00' &&
        f.dueTimeEnd === '10:00' &&
        !f.title.trim();
      if (!untouched) return f;
      const todayKey = formatWallClockYmd(new Date(), timeZone);
      return { ...f, dueDate: todayKey, endDate: todayKey };
    });
    activityFormSeededTzRef.current = timeZone;
  }, [timeZone, timeZoneReady, editingActivity]);

  React.useEffect(() => {
    if (!activityTypesLoaded || activityTypesFromApi.length === 0) return;
    setActivityForm((f) => {
      if (f.checklist_id) return f;
      const next = getDefaultChecklistIdForActivityType(activityTypesFromApi, f.activity_type);
      if (!next) return f;
      return { ...f, checklist_id: next };
    });
  }, [activityTypesLoaded, activityTypesFromApi]);

  const activityChecklistOptions = React.useMemo(
    () => buildChecklistSelectOptions(checklistsForSelect, activityForm.activity_type),
    [checklistsForSelect, activityForm.activity_type],
  );
  const checklistNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const c of checklistsForSelect) map.set(c.id, c.name);
    return map;
  }, [checklistsForSelect]);

  const activityCalendarActivities = React.useMemo(
    () =>
      filterActivitiesForEinsatzteam(scheduledActivities, activityForm.assignee_employee_ids).filter(
        (a) => a.id !== editingActivity?.id,
      ),
    [scheduledActivities, activityForm.assignee_employee_ids, editingActivity?.id],
  );

  const activityRepeating = (activityForm.recurrence ?? defaultRecurrenceForm(activityForm.dueDate)).freq !== 'none';
  const activityEndYmd = activityRepeating ? activityForm.dueDate : activityForm.endDate;
  const availability = useAssigneeAvailability({
    dueAt: activityForm.dueDate
      ? localWallTimeToIso(activityForm.dueDate, activityForm.dueTimeStart || '00:00', timeZone)
      : null,
    endAt: activityEndYmd ? localWallTimeToIso(activityEndYmd, activityForm.dueTimeEnd || '23:59', timeZone) : null,
    excludeActivityId: editingActivity?.id ?? null,
    enabled: timeZoneReady,
  });
  const activityEmployeeSelectOptions = React.useMemo(
    () => activityEmployeeBaseOptions.map((o) => ({ ...o, unavailable: availabilityBadge(availability[o.id]) })),
    [activityEmployeeBaseOptions, availability],
  );
  const unavailable = React.useMemo(
    () =>
      unavailableAssignees(
        availability,
        activityForm.assignee_employee_ids,
        (id) => activityEmployeeBaseOptions.find((o) => o.id === id)?.name ?? 'Mitarbeiter',
      ),
    [availability, activityForm.assignee_employee_ids, activityEmployeeBaseOptions],
  );

  const shiftActivityFormDay = React.useCallback(
    (delta: number) => {
      setActivityForm((f) => {
        const next = addCalendarDaysYmd(f.dueDate || formatWallClockYmd(new Date(), timeZone), delta);
        return { ...f, dueDate: next, endDate: next };
      });
    },
    [timeZone],
  );

  const activityDayCalendarEl = (
    <ActivityDayCalendar
      displayDate={activityForm.dueDate || formatWallClockYmd(new Date(), timeZone)}
      onPrevDay={() => shiftActivityFormDay(-1)}
      onNextDay={() => shiftActivityFormDay(1)}
      activities={activityCalendarActivities}
      activityTypes={activityTypesList}
      timeZone={timeZone}
      unavailable={unavailable}
      previewSlot={{
        title:
          activityForm.title.trim() ||
          activityTypesList.find((t) => t.id === activityForm.activity_type)?.label ||
          'Neue Aktivität',
        startTime: activityForm.dueTimeStart,
        endTime: activityForm.dueTimeEnd,
        activityType: activityForm.activity_type,
      }}
    />
  );

  const activityProjectAddressHint = (lead?.baustelle?.address ?? lead?.site_address ?? '').trim();

  const activityAddressFieldEl = (
    <div>
      <label className="mb-1 block text-xs font-medium text-app-text-muted">
        Adresse
      </label>
      <AddressAutocompleteInput
        value={activityForm.site_address}
        onChange={(v) => setActivityForm((f) => ({ ...f, site_address: v }))}
        placeholder="Adresse für diesen Termin"
        inputClassName={APP_INPUT_CLASS}
      />
      {activityForm.site_address.trim() && activityForm.site_address.trim() !== activityProjectAddressHint ? (
        <button
          type="button"
          className="mt-1 text-xs font-medium text-pipedrive-blue hover:underline"
          onClick={() => setActivityForm((f) => ({ ...f, site_address: activityProjectAddressHint }))}
        >
          Projektadresse verwenden
        </button>
      ) : null}
    </div>
  );

  const [addingActivity, setAddingActivity] = React.useState(false);
  const [showCreateActivityModal, setShowCreateActivityModal] = React.useState(false);

  const loadActivities = React.useCallback(() => {
    if (!lead) return;
    fetch(`/api/leads/${lead.id}/activities`)
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => setActivities(Array.isArray(list) ? list : []))
      .catch(() => setActivities([]));
  }, [lead?.id]);

  React.useEffect(() => {
    if (lead?.id) loadActivities();
  }, [lead?.id, loadActivities]);

  const resetActivityForm = React.useCallback(() => {
    setEditingActivity(null);
    setActivitySaveError(null);
    const todayKey = formatWallClockYmd(new Date(), timeZone);
    const initialType = activityTypesList[0]?.id ?? 'call';
    setActivityForm({
      title: '',
      activity_type: initialType,
      checklist_id: getDefaultChecklistIdForActivityType(activityTypesList, initialType),
      dueDate: todayKey,
      dueTimeStart: '09:00',
      dueTimeEnd: '10:00',
      endDate: todayKey,
      scheduling_status: 'geplant',
      notes: '',
      site_address: (lead?.baustelle?.address ?? lead?.site_address ?? '').trim(),
      assignee_user_ids: [],
      assignee_employee_ids: [],
      recurrence: defaultRecurrenceForm(todayKey),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeZone, activityTypesList, lead?.id, lead?.site_address, lead?.baustelle?.address]);

  const fillFormFromActivity = React.useCallback(
    (activity: LeadActivity) => {
      setActivitySaveError(null);
      const due = isoToLocalDateAndTime(activity.due_at ?? null, timeZone);
      let endDate = due.dateKey;
      let endTime: string;
      if (activity.end_at) {
        const end = isoToLocalDateAndTime(activity.end_at, timeZone);
        endDate = end.dateKey;
        endTime = end.time;
      } else {
        const [hh, mm] = due.time.split(':').map((x) => Number.parseInt(x, 10));
        const startMin = (Number.isFinite(hh) ? hh : 9) * 60 + (Number.isFinite(mm) ? mm : 0);
        const endMin = Math.min(24 * 60 - 1, startMin + 60);
        endTime = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
      }
      setActivityForm({
        title: activity.title,
        activity_type: activity.activity_type || (activityTypesList[0]?.id ?? 'call'),
        checklist_id: activity.checklist_id ?? null,
        dueDate: due.dateKey,
        dueTimeStart: due.time,
        dueTimeEnd: endTime,
        endDate,
        scheduling_status: (activity.scheduling_status as SchedulingStatus) || 'geplant',
        notes: activity.notes || '',
        site_address: String(activity.site_address ?? '').trim() || (lead?.baustelle?.address ?? lead?.site_address ?? '').trim(),
        assignee_user_ids: (activity.assignee_users ?? []).map((u) => u.user_id),
        assignee_employee_ids: (activity.assignee_employees ?? []).map((e) => e.id),
        recurrence: recurrenceFormFromStored(activity.recurrence, due.dateKey),
      });
      setEditingActivity(activity);
    },
    [timeZone, activityTypesList, lead?.baustelle?.address, lead?.site_address],
  );

  const { intercept: interceptConflict, dialog: activityConflictDialog } = useAssigneeConflictConfirm();

  const addActivity = React.useCallback(async (confirmConflicts = false): Promise<boolean> => {
    if (!lead || !activityForm.title.trim()) return false;
    setAddingActivity(true);
    setActivitySaveError(null);
    try {
      const userIds =
        activityForm.assignee_user_ids.length > 0
          ? activityForm.assignee_user_ids
          : leadAssigneeUserIds;
      const repeating = (activityForm.recurrence ?? defaultRecurrenceForm(activityForm.dueDate)).freq !== 'none';
      const endYmd = repeating ? activityForm.dueDate : activityForm.endDate;
      const due_at = activityForm.dueDate
        ? localWallTimeToIso(activityForm.dueDate, activityForm.dueTimeStart || '00:00', timeZone)
        : null;
      const end_at = endYmd
        ? localWallTimeToIso(endYmd, activityForm.dueTimeEnd || '23:59', timeZone)
        : null;
      const recurrence = recurrencePayloadFromForm(
        activityForm.recurrence ?? defaultRecurrenceForm(activityForm.dueDate),
        activityForm.dueDate,
      );
      const res = await fetch(`/api/leads/${lead.id}/activities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: activityForm.title.trim(),
          activity_type: activityForm.activity_type,
          checklist_id: activityForm.checklist_id || null,
          due_at: due_at || undefined,
          end_at: end_at || undefined,
          scheduling_status: activityForm.scheduling_status,
          notes: activityForm.notes.trim() || undefined,
          site_address: activityForm.site_address.trim() || null,
          assignee_user_ids: userIds,
          assignee_employee_ids: activityForm.assignee_employee_ids,
          ...(recurrence ? { recurrence } : {}),
          ...(confirmConflicts ? { confirm_conflicts: true } : {}),
        }),
      });
      const retry = async () => {
        if (await addActivity(true)) setShowCreateActivityModal(false);
      };
      if (await interceptConflict(res, retry)) return false;
      if (res.ok) {
        loadActivities();
        resetActivityForm();
        return true;
      }
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setActivitySaveError(body.error || 'Aktivität konnte nicht erstellt werden.');
      return false;
    } finally {
      setAddingActivity(false);
    }
  }, [lead, activityForm, leadAssigneeUserIds, timeZone, resetActivityForm, loadActivities, interceptConflict]);

  const updateActivity = React.useCallback(
    async (activityId: string, confirmConflicts = false) => {
      if (!lead || !activityForm.title.trim()) return;
      setAddingActivity(true);
      setActivitySaveError(null);
      try {
        const repeating = (activityForm.recurrence ?? defaultRecurrenceForm(activityForm.dueDate)).freq !== 'none';
        const endYmd = repeating ? activityForm.dueDate : activityForm.endDate;
        const due_at = activityForm.dueDate
          ? localWallTimeToIso(activityForm.dueDate, activityForm.dueTimeStart || '00:00', timeZone)
          : null;
        const end_at = endYmd
          ? localWallTimeToIso(endYmd, activityForm.dueTimeEnd || '23:59', timeZone)
          : null;
        const recurrence = recurrencePayloadFromForm(
          activityForm.recurrence ?? defaultRecurrenceForm(activityForm.dueDate),
          activityForm.dueDate,
        );
        const res = await fetch(`/api/leads/${lead.id}/activities/${activityId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: activityForm.title.trim(),
            activity_type: activityForm.activity_type,
            checklist_id: activityForm.checklist_id || null,
            due_at: due_at || undefined,
            end_at: end_at || undefined,
            scheduling_status: activityForm.scheduling_status,
            notes: activityForm.notes.trim() || undefined,
            site_address: activityForm.site_address.trim() || null,
            assignee_user_ids: activityForm.assignee_user_ids,
            assignee_employee_ids: activityForm.assignee_employee_ids,
            // Preserve done flag when editing from the completed list.
            done: editingActivity?.done === true ? true : undefined,
            ...(editingActivity?.series_id || recurrence ? { recurrence: recurrence ?? null } : {}),
            ...(confirmConflicts ? { confirm_conflicts: true } : {}),
          }),
        });
        if (await interceptConflict(res, () => updateActivity(activityId, true))) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          setActivitySaveError(body.error || 'Aktivität konnte nicht gespeichert werden.');
          return;
        }
        const updated = (await res.json()) as LeadActivity | null;
        if (!updated || !updated.id) {
          setActivitySaveError('Aktivität konnte nicht gespeichert werden.');
          return;
        }
        loadActivities();
        resetActivityForm();
      } catch (e) {
        setActivitySaveError(e instanceof Error ? e.message : 'Aktivität konnte nicht gespeichert werden.');
      } finally {
        setAddingActivity(false);
      }
    },
    [lead, activityForm, timeZone, resetActivityForm, editingActivity?.done, editingActivity?.series_id, loadActivities, interceptConflict],
  );

  const toggleActivityDone = React.useCallback(
    async (activityId: string, done: boolean) => {
      if (!lead) return;
      try {
        const res = await fetch(`/api/leads/${lead.id}/activities/${activityId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ done }),
        });
        if (res.ok) {
          loadActivities();
          setActivityMenuOpen(null);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [lead, loadActivities],
  );

  /** Returns an error message, or null on success. Series need `scope` (see LeadActivityDeleteDialog). */
  const deleteActivity = React.useCallback(
    async (activityId: string, scope?: LeadActivityDeleteScope): Promise<string | null> => {
      if (!lead) return null;
      setActivityMenuOpen(null);
      try {
        const qs = scope ? `?scope=${scope}` : '';
        const res = await fetch(`/api/leads/${lead.id}/activities/${activityId}${qs}`, { method: 'DELETE' });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          return body.error || 'Löschen fehlgeschlagen.';
        }
        loadActivities();
        return null;
      } catch (e) {
        console.error(e);
        return e instanceof Error ? e.message : 'Löschen fehlgeschlagen.';
      }
    },
    [lead, loadActivities],
  );

  return {
    activities,
    loadActivities,
    instanceMembers,
    employeesForAssign,
    activityMenuOpen,
    setActivityMenuOpen,
    activityUserSelectOptions,
    activityEmployeeSelectOptions,
    scheduledActivities,
    activityTypesList,
    checklistsForSelect,
    editingActivity,
    activitySaveError,
    activityForm,
    setActivityForm,
    activityChecklistOptions,
    checklistNameById,
    activityCalendarActivities,
    shiftActivityFormDay,
    activityDayCalendarEl,
    activityAddressFieldEl,
    addingActivity,
    showCreateActivityModal,
    setShowCreateActivityModal,
    resetActivityForm,
    fillFormFromActivity,
    addActivity,
    updateActivity,
    toggleActivityDone,
    deleteActivity,
    activityConflictDialog,
  };
}

export type UseLeadActivitiesResult = ReturnType<typeof useLeadActivities>;
